import { FadeIn, MaskedLine } from "@/components/shared/Reveal";
import CTASection from "@/components/shared/CTASection";

const milestones = [
  ["1980", "The first compile", "JCL and COBOL at Liberty Mutual. Punch-card discipline, batch windows, and the revelation that a few hundred lines of code could move an entire insurance operation."],
  ["The mainframe years", "Systems built to last", "IMS, CICS, DB2, VSAM — the transactional backbone of the enterprise. Lessons in durability that no modern framework teaches."],
  ["The desktop rebellion", "Computing gets personal", "C++ and Visual Basic dragged logic out of the data center and onto the desk. Client/server taught the cost of distributing complexity."],
  ["The web arrives", "Everything connects", "The browser became the interface and the network became the computer. Applications escaped the building and never came back."],
  ["The platform shift", "Salesforce and the SaaS lesson", "Configuration began to rival code. Platforms proved the enterprise would rather assemble than author — when assembly works."],
  ["A decade at ServiceNow", "Solution Architect & Platform Developer", "Ten-plus years building on the Now Platform: App Engine, Workflow Design Studio, configurable workspaces, UI Builder, dashboards, REST APIs, integrations, and rapid prototyping."],
  ["Now", "The AI chapter", "AI Agents, Build Agent, and AI-assisted development. The tools change again. The craft endures."],
];

const About = () => (
  <main data-testid="about-page" className="bg-ink">
    <section className="relative pt-40 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <MaskedLine delay={0.1}>
          <p className="label-mono">About Nexus IPG</p>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-100">
          <MaskedLine delay={0.25}>Four decades of building.</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">Zero interest in stopping.</span>
          </MaskedLine>
        </h1>
      </div>
    </section>

    <section className="border-t hairline bg-ink-light">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeIn className="lg:col-span-5">
          <div className="relative overflow-hidden border hairline">
            <img
              src="https://images.unsplash.com/photo-1766941234616-ba978cb000e3?fm=jpg&q=85&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0"
              alt="1980-era CRT terminal with green phosphor screen"
              className="h-[480px] w-full object-cover heritage-image"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-amber-900/25 mix-blend-overlay" />
          </div>
          <p className="mt-4 font-mono-tech text-[11px] tracking-[0.25em] uppercase text-slate-500">
            Where it began // 1980
          </p>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <p className="font-serif text-2xl md:text-3xl text-amber-100/90 italic leading-snug">
            "A career measured in technology eras, not job titles."
          </p>
          <p className="mt-8 text-slate-400 font-light leading-relaxed text-lg">
            In 1980, our founder sat down at a Liberty Mutual terminal and wrote JCL and COBOL
            for a living. It was the start of a career that would trace the entire arc of
            enterprise computing — not as a spectator reading release notes, but as a builder
            shipping real systems on each successive wave.
          </p>
          <p className="mt-6 text-slate-400 font-light leading-relaxed text-lg">
            Nexus Intelligent Process Group exists because that arc taught one durable lesson:
            the technology always changes, and the fundamentals never do. Understand the people.
            Respect what already works. Choose the tool after you understand the problem.
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="border-t hairline bg-ink">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <FadeIn className="lg:col-span-4">
          <div
            data-testid="founder-portrait-frame"
            className="relative overflow-hidden border-2 border-amber-700/40 bg-ink-light aspect-[4/5]"
          >
            <img
              src="/assets/tom-cullen.png"
              alt="Tom Cullen, Founder & Principal of Nexus Intelligent Process Group"
              className="absolute inset-0 h-full w-full object-cover heritage-image"
            />
            <div className="absolute inset-0 bg-amber-900/15 mix-blend-overlay" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink/80 to-transparent" />
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-slate-200 whitespace-nowrap">
              Tom Cullen // Founder
            </span>
          </div>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
          <p className="label-mono mb-4">The Founder</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
            Tom Cullen
          </h2>
          <p className="mt-2 font-mono-tech text-xs tracking-[0.25em] uppercase text-amber-500/80">
            Founder & Principal
          </p>
          <p className="mt-6 text-slate-400 font-light leading-relaxed text-lg">
            Tom wrote his first production code — JCL and COBOL — at Liberty Mutual in 1980,
            and has been building enterprise software ever since. Across mainframes,
            client/server, the web, Salesforce, and more than a decade at ServiceNow as a
            Solution Architect and Platform Developer, one thing never changed: he still
            builds, hands-on, every day — now with AI Agents and AI-assisted development
            beside the decades of craft.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["ServiceNow Certified System Administrator", "ServiceNow Certified Application Developer", "45+ Years Building Enterprise Software"].map((c) => (
              <span
                key={c}
                data-testid={`founder-credential-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-mono-tech text-[10px] tracking-[0.15em] uppercase text-amber-400/80 border border-amber-800/40 px-3 py-2"
              >
                {c}
              </span>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="border-t hairline bg-ink-light">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36">
        <FadeIn className="mb-20">
          <p className="label-mono mb-4">The Long Arc</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 max-w-2xl leading-tight">
            A story told in systems, not job titles.
          </h2>
        </FadeIn>
        <div className="relative border-l border-amber-800/40 ml-2 md:ml-6 space-y-16">
          {milestones.map(([era, title, body], i) => (
            <FadeIn key={era} delay={0.05 * i} className="relative pl-10 md:pl-16">
              <span className="absolute -left-[7px] top-2 w-3.5 h-3.5 bg-ink border-2 border-amber-500/70 rotate-45" />
              <p className="font-mono-tech text-amber-500/80 text-xs tracking-[0.3em] uppercase">{era}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mt-2 mb-3">{title}</h3>
              <p className="text-slate-400 font-light leading-relaxed max-w-2xl">{body}</p>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t hairline bg-ink-light">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-3 gap-12">
        {[
          ["Seasoned", "Systems built in 1980 still run today. We bring that durability mindset to every modern build."],
          ["Current", "Certified on the platforms of now — ServiceNow CSA and CAD — and fluent in AI-assisted development."],
          ["Practical", "Evolution, not revolution. We have lived through enough revolutions to know how few of them stick."],
        ].map(([t, b], i) => (
          <FadeIn key={t} delay={0.1 * i} className="border-l-2 border-amber-700/60 pl-6 py-2">
            <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mb-3">{t}</h3>
            <p className="text-slate-400 font-light leading-relaxed text-sm">{b}</p>
          </FadeIn>
        ))}
      </div>
    </section>

    <CTASection
      title="Forty-five years of pattern recognition, on your problem."
      sub="We have seen your modernization challenge before — in several decades of clothing."
    />
  </main>
);

export default About;
